import { FC, useEffect, useState } from "react";
import { Box, CircularProgress, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import { toast } from "@vilocnv/allsetra-core";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";

// Data
import { useAppDispatch, useAppSelector, useReportCommonStates } from "hooks";
import {
  commonReportsDataFormatter,
  multipleRidesReportsFilterFormValidationSchema,
  convertDatesForFilterPayload,
  convertToNormalTime,
  reportTransformTimeForAPI,
  getStandardizedReportDates,
  reportsMilageRegistrationFormInitialValues
} from "app/data/helpers";
import {
  getResetFilterFormValues,
  buildReportFilterPayload
} from "app/data/helpers/ReportHelpers";
import { generateReportThunk } from "app/features/reports/reportsActions";
import {
  getAllKeysByAccountThunk,
  resetReportsFilterData,
  setReportsFilterData,
  setReportsDateValues
} from "app/features";
import { useTranslation } from "react-i18next";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import { selectAccountKeysState } from "app/data/selectors";
import ReportsCommonFilterForm from "components/forms/Reports/ReportsCommonFilterForm";

import MultipleRidesGoogleMap from "components/maps/MultipleRidesGoogleMap/MultipleRidesGoogleMap";
import { CenterTextBox } from "./Reports.styled";
import { MapStateProvider } from "components/maps/Map/v2/GoogleMap/MapStateContext";

const MultipleRides: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    handleOnChangeReportFilterForm,
    reportsHasExceededLimit,
    reportsFilterData,
    filterValues,
    setFilterValues,
    mergedObjectOptions,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates
  } = useReportCommonStates();

  const [openModal, setOpenModal] = useState(false);

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);
  const [apiPayload, setApiPayload] = useState(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? reportsFilterData
      : null
  );

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const multipleRidesFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    const startDate = new Date(dateValues.startDate);
    const endDate = new Date(dateValues.endDate);

    setFilterValues(values);

    const { formattedStartDate, formattedEndDate } =
      convertDatesForFilterPayload(dateValues);

    const payload = {
      ...(apiPayload ?? {}),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...values,
      object: values.assignedToAllObjects ? [] : values.object,
      reportType: 5,
      daysOfWeek:
        values.daysOfWeek.length === 7 ? [] : values.daysOfWeek.map(Number),
      startTime: reportTransformTimeForAPI(
        values.startTime + ":00",
        startDate,
        false
      ),
      endTime: reportTransformTimeForAPI(
        values.endTime + ":00",
        endDate,
        true,
        startDate
      )
    };

    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const formattedData = commonReportsDataFormatter(
    localReport,
    "name",
    dateFormat
  );

  const handleDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  };

  const handleDateRangeSubmit = () => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    let updatedFilterValues = { ...filterValues };

    if (filterValues.daysOfWeek) {
      updatedFilterValues = {
        ...filterValues,
        daysOfWeek:
          filterValues.daysOfWeek.length === 7
            ? []
            : filterValues.daysOfWeek.map(Number),
        startTime: reportTransformTimeForAPI(
          filterValues.startTime + ":00",
          dateValues.startDate,
          false
        ),
        endTime: reportTransformTimeForAPI(
          filterValues.endTime + ":00",
          dateValues.endDate,
          true,
          new Date(dateValues.startDate)
        )
      };
    }

    const payload = {
      ...(apiPayload ?? {}),
      startDate,
      endDate,
      ...updatedFilterValues,
      object: filterValues.assignedToAllObjects
        ? []
        : (filterValues.object ?? []),
      reportType: 5
    };

    setApiPayload(payload);
  };

  const handleLoadReport = () => {
    const payload = buildReportFilterPayload({
      values: filterValues,
      dateValues,
      reportType: 5,
      normalizeDates: false,
      startDateOverride: convertToNormalTime(dateValues.startDate),
      endDateOverride: convertToNormalTime(dateValues.endDate, true)
    });

    setApiPayload(payload);
    dispatch(setReportsFilterData(payload));
    dispatch(generateReportThunk(payload));
  };

  useEffect(() => {
    // dispatch(resetReports());
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId]);
  useEffect(() => {
    setLocalReport(report);
  }, [report]);

  useEffect(() => {
    if (report?.length && reportsHasExceededLimit) {
      const groupedRidesItems = report
        .flatMap((entry: any) => {
          return entry.items;
        })
        .reduce((acc: any, item: any) => {
          if (item?.rideLocations?.length > 0) {
            acc[item.rideUniqueId] = (acc[item.rideUniqueId] || 0) + 1;
          }
          return acc;
        }, {});

      const countItems = Object.entries(groupedRidesItems).length;

      if (countItems) {
        toast.info(
          t("reports.rideLimitExceed", {
            count: countItems
          })
        );
      }
    }
  }, [report, reportsHasExceededLimit]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.multipleRides")}
        disabled={reportsLoading}
        hideReportHistory={true}
        hideScheduleReports={true}
      />
      <Box mx={4}>
        <ReportsFilterBar
          openModal={openModal}
          setOpenModal={setOpenModal}
          hideExportButton={true}
          hideDateFormats={true}
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          disableExportButton={!formattedData?.length}
          reportType={5}
          reportExportTypeValue={2}
          hideScheduleButton
          handleLoadReport={handleLoadReport}
        />
        <ReportsCommonFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={multipleRidesFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={mergedObjectOptions}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          theme={theme}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          formInitialValues={getHydratedFormInitialValues(
            reportsMilageRegistrationFormInitialValues
          )}
          formValidations={multipleRidesReportsFilterFormValidationSchema}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(
                reportsMilageRegistrationFormInitialValues
              )
            );
          }}
          isMultipleRidesForm
        />
        {reportsLoading && (
          <CenterTextBox>
            <CircularProgress />
          </CenterTextBox>
        )}

        {!reportsLoading && localReport?.length > 0 && (
          <MapStateProvider
            height="calc(100vh - 170px)"
            disableNavigator={true}
            skipCurrentLocation={true}
            showSearch={true}
            showFilter={true}
            isDashboardMap={false}
            objectsMarker={false}
            objects={[]}
            initialZoomLevel={null}
          >
            <MultipleRidesGoogleMap objectsRides={localReport} />
          </MapStateProvider>
        )}

        {!reportsLoading && !localReport?.length && (
          <CenterTextBox>There are no records to display</CenterTextBox>
        )}
      </Box>
    </Box>
  );
};

export default MultipleRides;
